// src/index.js
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import './index.css';
import theme, { applyThemeCssVars } from './theme';

/**
 * Bootstraps CSS variables, meta tags, and UX helpers BEFORE React mounts.
 * Matches tokens & FX used by index.css and theme.js:
 *  - Applies all tokens from theme.js to :root (no FOUC)
 *  - Sets mobile address bar / color-scheme meta
 *  - Ensures viewport-fit=cover for iOS safe areas
 *  - Preconnects & preloads Inter for stable first paint
 *  - Preloads ambient/FX + divider assets referenced by index.css
 *  - Live-syncs prefers-reduced-motion -> --fx-motion-scale
 *  - Installs window.srAnnounce(...) for accessible status messages
 *  - Exposes window.reapplyTheme(nextTheme) for runtime palette swaps
 */
(function bootstrapTheme() {
  try {
    if (typeof document === 'undefined') return;

    const d = document;
    const de = d.documentElement;

    // Small CSS hooks
    de.classList.add('js-enabled');
    try { d.body.classList.add('is-booting'); } catch {}

    // 1) Push all theme CSS vars into :root (colors, ramps, assets, FX)
    applyThemeCssVars(de, theme); // aligns with current theme.js contract

    // 1a) Dark color-scheme for native controls
    de.setAttribute('data-theme', 'dark');
    de.style.colorScheme = 'dark';

    // 2) Meta helpers
    const ensureMeta = (name, content) => {
      let m = d.querySelector(`meta[name="${name}"]`);
      if (!m) {
        m = d.createElement('meta');
        m.setAttribute('name', name);
        d.head.appendChild(m);
      }
      if (m.content !== content) m.content = content;
      return m;
    };

    const bg = theme.colors?.background || '#070316';
    ensureMeta('theme-color', bg);     // Android/Chrome UI color, etc.
    ensureMeta('color-scheme', 'dark');

    // 3) Viewport (iOS safe areas)
    const DESIRED_VIEWPORT = 'width=device-width, initial-scale=1, viewport-fit=cover';
    let metaViewport = d.querySelector('meta[name="viewport"]');
    if (metaViewport) {
      if (!/viewport-fit=cover/.test(metaViewport.content || '')) {
        metaViewport.content = DESIRED_VIEWPORT;
      }
    } else {
      metaViewport = d.createElement('meta');
      metaViewport.setAttribute('name', 'viewport');
      metaViewport.setAttribute('content', DESIRED_VIEWPORT);
      d.head.appendChild(metaViewport);
    }

    // 3z) Control browser scroll restoration for SPA (App manages scrolling)
    try {
      const h = window.history;
      if (h && 'scrollRestoration' in h) h.scrollRestoration = 'manual';
    } catch {}

    // 3a) Viewport units + safe areas -> CSS vars
    const setViewportVars = () => {
      // Mobile 100vh fix: prefer visualViewport for dynamic toolbars
      let h = window.innerHeight;
      try {
        if (window.visualViewport && typeof window.visualViewport.height === 'number') {
          h = window.visualViewport.height;
        }
      } catch {}
      const vh = h * 0.01;
      de.style.setProperty('--vh', `${vh}px`);
      // Expose safe area insets (usable in CSS via var(--safe-top), etc.)
      try {
        de.style.setProperty('--safe-top', 'env(safe-area-inset-top)');
        de.style.setProperty('--safe-right', 'env(safe-area-inset-right)');
        de.style.setProperty('--safe-bottom', 'env(safe-area-inset-bottom)');
        de.style.setProperty('--safe-left', 'env(safe-area-inset-left)');
      } catch {}
    };
    setViewportVars();
    let __vhRaf = 0;
    const queueViewportUpdate = () => {
      if (__vhRaf) cancelAnimationFrame(__vhRaf);
      __vhRaf = requestAnimationFrame(setViewportVars);
    };
    window.addEventListener('resize', queueViewportUpdate);
    window.addEventListener('orientationchange', queueViewportUpdate);
    window.addEventListener('scroll', queueViewportUpdate, { passive: true });
    window.addEventListener('pageshow', queueViewportUpdate);
    document.addEventListener('visibilitychange', queueViewportUpdate);

    // 4) Fonts — preconnect + preload Inter (stable first paint)
    if (!d.querySelector('link[rel="preconnect"][href="https://fonts.gstatic.com"]')) {
      const pre = d.createElement('link');
      pre.rel = 'preconnect';
      pre.href = 'https://fonts.gstatic.com';
      pre.crossOrigin = 'anonymous';
      d.head.appendChild(pre);
    }
    if (!d.querySelector('link[rel="preconnect"][href="https://fonts.googleapis.com"]')) {
      const preCss = d.createElement('link');
      preCss.rel = 'preconnect';
      preCss.href = 'https://fonts.googleapis.com';
      preCss.crossOrigin = 'anonymous';
      d.head.appendChild(preCss);
    }
    if (!d.querySelector('link#font-inter')) {
      const font = d.createElement('link');
      font.id = 'font-inter';
      font.rel = 'preload';
      font.as = 'style';
      font.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&display=swap';
      font.onload = () => (font.rel = 'stylesheet');
      d.head.appendChild(font);
    }

    // 5) PRELOAD: critical ambient + FX assets (used above the fold)
    const preloadImage = (href) => {
      if (!href) return;
      try {
        if (d.querySelector(`link[rel="preload"][as="image"][href="${href}"]`)) return;
        const link = d.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = href;
        d.head.appendChild(link);
      } catch {}
    };

    // Ambient/background & core UI FX from theme.js (consumed by index.css)
    const fx = theme.fx || {};
    [
      fx.gridWave?.url,
      fx.ambientGrid?.url,
      fx.sparklesAmbient?.url,
      fx.buttonGlint?.url,
      fx.cardGlow?.url,
      fx.progressShine?.url,
      fx.focusUnderline?.url,
      fx.dividerRainbowThin?.url,
      fx.dividerRainbowThin?.url1,
      fx.dividerRainbowThin?.url2,
    ].filter(Boolean).forEach(preloadImage);

    // 6) Live‑sync reduced‑motion -> --fx-motion-scale (index.css respects this)
    try {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      const applyMotion = () => de.style.setProperty('--fx-motion-scale', mq.matches ? '0' : '1');
      applyMotion();
      if (mq.addEventListener) mq.addEventListener('change', applyMotion);
      else if (mq.addListener) mq.addListener(applyMotion);
    } catch {}

    // 6a) Input modality detection: touch vs pointer + keyboard focus state
    try {
      const w = window;
      const updatePointer = () => {
        const coarse = !!(w.matchMedia && w.matchMedia('(pointer: coarse)').matches);
        de.classList.toggle('mod-touch', coarse);
        de.classList.toggle('mod-pointer', !coarse);
      };
      updatePointer();
      if (w.matchMedia) {
        const m = w.matchMedia('(pointer: coarse)');
        if (m.addEventListener) m.addEventListener('change', updatePointer);
        else if (m.addListener) m.addListener(updatePointer);
      }
      let usingKeyboard = false;
      const setUsingKeyboard = (val) => {
        if (usingKeyboard === val) return;
        usingKeyboard = val;
        de.classList.toggle('using-keyboard', usingKeyboard);
      };
      const onKeyDown = (e) => {
        const k = e.key;
        if (k === 'Tab' || k === 'ArrowDown' || k === 'ArrowUp' || k === 'ArrowLeft' || k === 'ArrowRight') {
          setUsingKeyboard(true);
        }
      };
      const onPointerDown = () => setUsingKeyboard(false);
      w.addEventListener('keydown', onKeyDown, true);
      w.addEventListener('mousedown', onPointerDown, true);
      w.addEventListener('touchstart', onPointerDown, { passive: true, capture: true });
    } catch {}

    // 7) Polite live region for screen readers (non‑conflicting id)
    let sr = d.getElementById('sr-updates') || d.getElementById('sr-bootstrap-updates');
    if (!sr) {
      sr = d.createElement('div');
      sr.id = 'sr-bootstrap-updates';
      sr.setAttribute('role', 'status');
      sr.setAttribute('aria-live', 'polite');
      sr.setAttribute('aria-atomic', 'true');
      sr.setAttribute(
        'style',
        'position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;clip:rect(0 0 0 0);clip-path:inset(50%);overflow:hidden;white-space:nowrap;'
      );
      d.body.appendChild(sr);
    }
    window.srAnnounce = (msg) => {
      try {
        const target = d.getElementById('sr-updates') || d.getElementById('sr-bootstrap-updates');
        if (!target) return;
        target.textContent = '';
        setTimeout(() => { target.textContent = String(msg || ''); }, 0);
      } catch {}
    };

    // Assertive alerts (e.g., errors) for screen readers
    window.srAlert = (msg) => {
      try {
        let live = d.getElementById('sr-alerts');
        if (!live) {
          live = d.createElement('div');
          live.id = 'sr-alerts';
          live.setAttribute('role', 'alert');
          live.setAttribute('aria-live', 'assertive');
          live.setAttribute('aria-atomic', 'true');
          live.setAttribute(
            'style',
            'position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;clip:rect(0 0 0 0);clip-path:inset(50%);overflow:hidden;white-space:nowrap;'
          );
          d.body.appendChild(live);
        }
        live.textContent = '';
        setTimeout(() => { live.textContent = String(msg || ''); }, 0);
      } catch {}
    };

    // 8) Expose helper to reapply theme vars at runtime
    window.reapplyTheme = (next = theme) => {
      applyThemeCssVars(de, next);
      const newBg = next?.colors?.background || bg;
      const m = d.querySelector('meta[name="theme-color"]');
      if (m && m.content !== newBg) m.content = newBg;
    };
  } catch (e) {
    if (process.env.NODE_ENV === 'development') console.warn('[theme bootstrap] init failed:', e);
  }
})();

// ------------------------------ React Mount ------------------------------
const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container #root not found');
}
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

// Mark ready after first paint (allows CSS to fade in ambient FX if desired)
try {
  requestAnimationFrame(() => {
    document.body.classList.remove('is-booting');
    document.body.classList.add('is-ready');
  });
} catch {}

// Farcaster Mini App: hide splash screen by calling sdk.actions.ready()
// We lazy-load the SDK at runtime so regular web builds aren't affected.
(function callMiniAppReady() {
  try {
    // Use ESM CDN to avoid adding a dependency; safely no-ops if not in a Mini App.
    import(/* webpackIgnore: true */ 'https://esm.sh/@farcaster/miniapp-sdk').then(async (mod) => {
      const sdk = mod?.sdk;
      if (sdk && sdk.actions && typeof sdk.actions.ready === 'function') {
        try {
          await sdk.actions.ready();
          if (process.env.NODE_ENV === 'development') console.log('[MiniApp] sdk.actions.ready() called');
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.warn('[MiniApp] ready() failed:', e);
        }
      }
    }).catch(() => {
      // ignore if SDK can't be loaded (non-Mini App context)
    });
  } catch {
    // ignore environments that don't support dynamic import of external modules
  }
})();
